// client/src/pages/GalleryAdmin.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/GalleryAdmin.css';

// Icônes SVG inline
const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const IconImages = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const GalleryAdmin = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    actif: true,
    ordre: 0
  });
  const [editMode, setEditMode] = useState(false);
  const [currentGalleryId, setCurrentGalleryId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback
  const defaultImage = '/images/gallery-default.png';
  
  // Charger les galeries
  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/gallery/admin');
        setGalleries(response.data.galleries);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des galeries:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    fetchGalleries();
  }, []);
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (galleryId) => {
    console.log(`Erreur de chargement d'image pour la galerie ${galleryId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [galleryId]: true
    }));
  };
  
  // Ouvrir le modal en mode création
  const openCreateModal = () => {
    setFormData({
      titre: '',
      description: '',
      actif: true,
      ordre: 0
    });
    setEditMode(false);
    setCurrentGalleryId(null);
    setShowModal(true);
  };
  
  // Ouvrir le modal en mode édition
  const openEditModal = (gallery) => {
    setFormData({
      titre: gallery.titre,
      description: gallery.description || '',
      actif: gallery.actif,
      ordre: gallery.ordre || 0
    });
    setEditMode(true);
    setCurrentGalleryId(gallery._id);
    setShowModal(true);
  };
  
  // Fermer le modal
  const closeModal = () => {
    setShowModal(false);
  };
  
  // Gérer les changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Mettre à jour une galerie existante
        const response = await axios.put(`/api/gallery/${currentGalleryId}`, formData);
        
        // Mettre à jour l'affichage
        setGalleries(galleries.map(gallery => 
          gallery._id === currentGalleryId ? response.data : gallery
        ));
        
        setSuccessMessage('Galerie mise à jour avec succès');
      } else {
        // Créer une nouvelle galerie
        const response = await axios.post('/api/gallery', formData);
        
        // Ajouter à la liste
        setGalleries([...galleries, response.data]);
        
        setSuccessMessage('Galerie créée avec succès');
      }
      
      // Fermer le modal
      closeModal();
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de la galerie:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Supprimer une galerie
  const handleDelete = async (id, titre) => {
    // Demander confirmation
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la galerie "${titre}" et toutes ses images ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/gallery/${id}`);
      
      // Mettre à jour la liste
      setGalleries(galleries.filter(gallery => gallery._id !== id));
      
      setSuccessMessage('Galerie supprimée avec succès');
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de la galerie:', err);
      setError(`Erreur: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Utiliser la route API pour les images
  const getGalleryCoverUrl = (gallery) => {
    if (!gallery.couverture || imageErrors[gallery._id]) return defaultImage;
    
    // Utiliser la nouvelle route API avec un timestamp pour éviter le cache
    const timestamp = new Date().getTime();
    return `/api/gallery/thumbnail/${gallery.couverture}?t=${timestamp}`;
  };
  
  return (
    <div className="gallery-admin-page">
      <div className="gallery-admin-header">
        <h1>Gestion des Galeries d'Images</h1>
        <button className="create-gallery-button" onClick={openCreateModal}>
          + Nouvelle Galerie
        </button>
            <Link to="/admin/gallery/download-stats" className="stats-button">
      Statistiques de téléchargement
    </Link>
      </div>
      
      {/* Messages */}
      {successMessage && <div className="message success">{successMessage}</div>}
      {error && <div className="message error">{error}</div>}
      
      {/* Liste des galeries */}
      {loading ? (
        <div className="gallery-loading">Chargement des galeries...</div>
      ) : galleries.length === 0 ? (
        <div className="no-galleries">
          <p>Aucune galerie disponible</p>
          <button className="create-first-gallery" onClick={openCreateModal}>
            Créer votre première galerie
          </button>
        </div>
      ) : (
        <div className="galleries-grid">
          {galleries.map(gallery => (
            <div key={gallery._id} className={`gallery-card ${!gallery.actif ? 'inactive' : ''}`}>
              <div className="gallery-card-image">
                <img 
                  src={getGalleryCoverUrl(gallery)} 
                  alt={gallery.titre}
                  onError={() => handleImageError(gallery._id)}
                />
                {!gallery.actif && <div className="inactive-badge">Inactive</div>}
              </div>
              <div className="gallery-card-content">
                <h3 className="gallery-title">{gallery.titre}</h3>
                <p className="gallery-description">
                  {gallery.description ? 
                    (gallery.description.length > 100 ? 
                      `${gallery.description.substring(0, 100)}...` : 
                      gallery.description) : 
                    'Aucune description'}
                </p>
                <div className="gallery-meta">
                  <span className="gallery-order">Ordre: {gallery.ordre}</span>
                </div>
                <div className="gallery-actions">
                  <Link 
                    to={`/admin/gallery/${gallery._id}/images`}
                    className="manage-images-button"
                    title="Gérer les images"
                  >
                    <IconImages /> Images
                  </Link>
                  <button 
                    className="edit-button"
                    onClick={() => openEditModal(gallery)}
                    title="Modifier la galerie"
                  >
                    <IconEdit /> Modifier
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDelete(gallery._id, gallery.titre)}
                    title="Supprimer la galerie"
                  >
                    <IconDelete /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal pour créer/éditer une galerie */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editMode ? 'Modifier la galerie' : 'Créer une nouvelle galerie'}</h2>
            <form onSubmit={handleSubmit} className="gallery-form">
              <div className="form-group">
                <label>Titre</label>
                <input 
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Ordre d'affichage</label>
                <input 
                  type="number"
                  name="ordre"
                  value={formData.ordre}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox"
                    name="actif"
                    checked={formData.actif}
                    onChange={handleInputChange}
                  />
                  Galerie active
                </label>
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="save-button">
                  {editMode ? 'Mettre à jour' : 'Créer la galerie'}
                </button>
                <button type="button" className="cancel-button" onClick={closeModal}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryAdmin;